import React from "react";
import ReactDOM from "react-dom/client";
import "@/design/workbench.css";
import "@/design/info_pages.css";
// The Draft80 design system. Loaded before the older stylesheets' consumers so
// a component can use a token without depending on import order elsewhere.
import "@/design/tokens.css";
import "@/design/postcard.css";
import "@/design/home.css";
import "@/design/day25.css";
import "@/design/flow.css";
// Global keyboard/focus accessibility layer (WCAG 2.2 AA): high-visibility
// :focus-visible rings on every interactive element. Loaded once, globally,
// after the other design stylesheets.
import "@/design/a11y.css";
import SiteRouter from "@/design/site";

// Marketing routes are prerendered to static HTML (scripts/prerender.js) so
// crawlers that do not run JavaScript get real content. That markup also
// paints immediately, then React renders over it.
//
// Deliberately createRoot, not hydrateRoot. The prerenderer captures the live
// DOM rather than doing true SSR, and DOM serialisation merges adjacent text
// nodes — JSX `{" "}` between elements produces separate nodes that real SSR
// preserves with <!-- --> separators. Hydration therefore reports a text
// mismatch (React #418) and regenerates the tree anyway. Rendering fresh gets
// the same result without logging an error on every prerendered page. Switch
// to hydrateRoot only alongside real server rendering.
// Activate the preloaded self-hosted font sheet without a render-blocking
// head stylesheet or an inline onload handler (the production CSP forbids it).
const fontStyles = document.getElementById("cc-font-styles");
if (fontStyles) fontStyles.rel = "stylesheet";

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <SiteRouter />
  </React.StrictMode>,
);

// Native shell (Capacitor, Android/iOS): the shell injects window.Capacitor
// before page code runs. On the web it stays undefined, nothing below changes,
// and the native module is a lazy chunk the browser never fetches. Inside the
// shell it wires the Android back button and deep links (src/native/appShell).
const runningInNativeShell =
  typeof window !== "undefined" && Boolean(window.Capacitor);
if (runningInNativeShell) {
  import("./native/appShell")
    .then(({ initNativeShell }) => initNativeShell())
    .catch(() => {});
}

// PWA: app-shell service worker (offline navigation fallback + install support).
// Skipped inside the native shell: app updates ship with the binary, and a
// service worker there could pin stale web assets across an app upgrade.
if (
  process.env.NODE_ENV === "production" &&
  "serviceWorker" in navigator &&
  !runningInNativeShell
) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
